import {
  Box,
  Button,
  Flex,
  Group,
  NumberInput,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { CarForm } from "../interfaces/CreateCarForm.types";

import { Dropdown, FormField } from "@/components/elements";
import { ConditionsTooltipContent } from "@/components/elements/TooltipContent";
import { usePlansUser } from "@/services/plans/get-user-plan";

import type { ElementType, FC } from "react";

interface Props {
  WrapperForFields: ElementType;
  wrapperProps: Record<string, unknown>;
  form: CarForm;
  showLessElements: boolean;
  activeStep: number;
  onCancel: (() => void) | undefined;
  loading: boolean;
  nextStep: () => void;
}

const Step3: FC<Props> = ({
  WrapperForFields,
  wrapperProps,
  form,
  showLessElements,
  activeStep,
  onCancel,
  loading,
  nextStep,
}) => {
  const { t } = useTranslation();

  const { data: plans } = usePlansUser();
  const isFreePlan = plans?.name === "starter";

  return (
    <Stack gap="md">
      <Title order={4} mb="sm">
        {t("components.CreateCarForm.titles.extraOptionalDetails")}
      </Title>

      <WrapperForFields {...wrapperProps}>
        <TextInput
          label={t("car.licence_plate")}
          placeholder={t("components.CreateCarForm.placeholders.licence_plate")}
          {...form.getInputProps("licence_plate")}
        />
        <TextInput
          label={t("car.country_of_registration")}
          placeholder={t("components.CreateCarForm.placeholders.country_of_registration")}
          {...form.getInputProps("country_of_registration")}
        />
      </WrapperForFields>

      <WrapperForFields {...wrapperProps}>
        <FormField>
          <TextInput
            label={t("car.vin")}
            placeholder={t("components.CreateCarForm.placeholders.vin")}
            {...form.getInputProps("vin")}
          />
        </FormField>

        <FormField>
          <TextInput
            label={t("car.color")}
            placeholder={t("components.CreateCarForm.placeholders.color")}
            {...form.getInputProps("color")}
          />
        </FormField>
      </WrapperForFields>
      <WrapperForFields {...wrapperProps}>
        <FormField>
          <TextInput
            label={t("car.interior_colors")}
            placeholder={t("components.CreateCarForm.placeholders.interior_colors")}
            {...form.getInputProps("interior_colors")}
          />
        </FormField>
        <Dropdown
          label={t("car.condition")}
          tooltip={<ConditionsTooltipContent />}
          disabled={isFreePlan}
          clearable
          showLessElements={showLessElements}
          placeholder={t("components.CreateCarForm.placeholders.condition")}
          data={[
            { value: "superb", label: t("car.conditions.superb") },
            { value: "excellent", label: t("car.conditions.excellent") },
            { value: "good", label: t("car.conditions.good") },
            { value: "fair", label: t("car.conditions.fair") },
          ]}
          value={form.values.condition}
          onChange={(value) => {
            // Check that it is one of the allowed options
            const allowedValues = ["superb", "excellent", "good", "fair"] as const;
            const safeValue = allowedValues.includes(value as any)
              ? (value as (typeof allowedValues)[number])
              : undefined;

            form.setFieldValue("condition", safeValue);
          }}
          error={form.errors.condition}
        />
      </WrapperForFields>

      <WrapperForFields {...wrapperProps}>
        <NumberInput
          label={t("car.number_of_previous_owner")}
          placeholder={t("components.CreateCarForm.placeholders.number_of_previous_owner")}
          min={0}
          {...form.getInputProps("number_of_previous_owner")}
        />

        <Box />
      </WrapperForFields>

      <Textarea
        label={t("car.special_equipment")}
        placeholder={t("components.CreateCarForm.placeholders.special_equipment")}
        minRows={4}
        {...form.getInputProps("special_equipment")}
      />

      <Group justify="space-between" mt="2em">
        <Text c="var(--mantine-color-primaryDark-5)" fw={500} fz="sm">
          Step {activeStep + 1} of 4
        </Text>

        <Flex justify="end" gap="md">
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={loading}
              aria-label={t("forms.cancelButton")}
              c="dark"
              miw={100}
            >
              {t("forms.cancelButton")}
            </Button>
          )}

          <Button type="submit" onClick={nextStep} aria-label={t("forms.create")} miw={100}>
            {t("forms.next")}
          </Button>
        </Flex>
      </Group>
    </Stack>
  );
};

export default Step3;
